import { Button, MoreButton, Space } from '@/components';
import { useMe } from '@/features/authentication/_hooks/me';
import { useNavigateToWorkflowProject } from '@/features/workflow/hooks/navigate-to-workflow';
import { useWorkflowStackSiderView } from '@/features/workflow/hooks/stack-sider-view';
import {
  useDeleteWorkflow,
  useWorkflow,
} from '@/features/workflow/hooks/workflow';
import { useMessage } from '@/providers';
import type { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useMatch } from 'react-router';
import invariant from 'tiny-invariant';

export const WorkflowDetailsHeader: FC = () => {
  const { t } = useTranslation();
  const stackSiderView = useWorkflowStackSiderView();

  const siderConfig = stackSiderView.lastItem?.config;

  invariant(
    siderConfig?.viewType === 'workflow-details',
    t('unreachable-code'),
  );

  const isWorkflowPage = useMatch('/workflow');
  const { openMessage } = useMessage();
  const navigateToWorkflowProject = useNavigateToWorkflowProject();

  const me = useMe();
  const workflow = useWorkflow(siderConfig.id);

  const { mutateAsync: deleteWorkflow } = useDeleteWorkflow({
    projectId:
      workflow.data?.key === 'project' ? workflow.data.objectId : undefined,
  });

  const canMutate =
    siderConfig.stageViewType === 'list' &&
    me.data &&
    workflow.data &&
    (me.data.id === workflow.data.authorId ||
      workflow.data.assigneeIds.includes(me.data.id));

  return (
    <Space>
      {isWorkflowPage && workflow.data && workflow.data?.key === 'project' && (
        <Button
          type="link"
          size="small"
          onClick={() => {
            navigateToWorkflowProject({
              projectId: workflow.data.objectId,
            });
          }}
        >
          {t('navigate-to-project')}
        </Button>
      )}
      {canMutate && (
        <>
          <Button
            type="primary"
            onClick={() => {
              stackSiderView.push({
                payload: {
                  config: {
                    viewType: 'workflow-form',
                    isEditing: true,
                    target: siderConfig.target,
                    id: siderConfig.id,
                  },
                },
                target: stackSiderView.target,
              });
            }}
          >
            {t('edit')}
          </Button>
          <MoreButton
            items={[
              {
                key: 'delete',
                label: t('delete'),
                danger: true,
                onClick: async () => {
                  try {
                    await deleteWorkflow({
                      id: siderConfig.id,
                    });
                    openMessage('success', t('message-delete-completed'));
                    stackSiderView.pop();
                  } catch {
                    openMessage('error', t('message-delete-failed'));
                  }
                },
              },
            ]}
          />
        </>
      )}
    </Space>
  );
};
